import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import type { PublicCmsSectionResponse } from '../../api/cmsTypes';

/**
 * Renderer for published CMS sections (ata #34). Mirrors the visual language of the hand-built
 * Landing.tsx/About.tsx design (gradients, icon cards, numbered steps, elevated CTA) so that
 * publishing content from /admin/cms doesn't visually "deformat" the public page — see the
 * incident where a publish replaced the polished static design with a plain generic layout.
 * Landing.tsx/About.tsx fall back to their existing hardcoded design when no CMS sections exist
 * for the page yet; this only activates once an admin has authored+published real content.
 */

function safeParse<T>(json: string | null): T | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

export interface HeroContent {
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
  ctaHref?: string;
}

export interface ItemsContent {
  title?: string;
  subtitle?: string;
  items?: { title?: string; description?: string }[];
}

export interface CtaContent {
  title?: string;
  subtitle?: string;
  buttonLabel?: string;
  buttonHref?: string;
}

export interface RichTextContent {
  title?: string;
  body?: string;
}

export interface VideoContent {
  title?: string;
  videoUrl?: string;
}

/** Shared layout controls (plan Fase E) — same shape for every section type. */
export interface LayoutConfig {
  columns?: number;
  alignment?: 'left' | 'center' | 'right';
  backgroundColor?: string;
}

function parseLayout(json: string | null): LayoutConfig {
  return safeParse<LayoutConfig>(json) ?? {};
}

function alignItemsFor(alignment: LayoutConfig['alignment']): 'flex-start' | 'center' | 'flex-end' {
  return alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center';
}

const HeroSection: React.FC<{ content: HeroContent; layout: LayoutConfig }> = ({ content, layout }) => {
  const theme = useTheme();
  return (
    <Box
      data-testid="cms-hero"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background:
          layout.backgroundColor ||
          'background.default',
        pt: { xs: 10, md: 14 },
        pb: { xs: 8, md: 10 },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'none',
        }}
      />
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={3} alignItems={alignItemsFor(layout.alignment)} textAlign={layout.alignment ?? 'center'}>
          {content.title && (
            <Typography
              variant="h1"
              sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, fontWeight: 600, letterSpacing: 0 }}
            >
              {content.title}
            </Typography>
          )}
          {content.subtitle && (
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, lineHeight: 1.6, maxWidth: 640 }}>
              {content.subtitle}
            </Typography>
          )}
          {content.ctaLabel && (
            <Button
              variant="contained"
              size="large"
              href={content.ctaHref}
              endIcon={<ArrowForwardIcon />}
              sx={{
                backgroundColor: theme.palette.primary.main,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 700,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              {content.ctaLabel}
            </Button>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

const SectionHeading: React.FC<{ title?: string; subtitle?: string; alignment?: LayoutConfig['alignment'] }> = ({
  title,
  subtitle,
  alignment,
}) => (
  <Stack spacing={2} alignItems={alignItemsFor(alignment)} textAlign={alignment ?? 'center'} mb={{ xs: 6, md: 8 }}>
    {title && (
      <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, fontWeight: 700, color: 'text.primary' }}>
        {title}
      </Typography>
    )}
    {subtitle && (
      <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, lineHeight: 1.7 }}>
        {subtitle}
      </Typography>
    )}
  </Stack>
);

/** FEATURES/CARDS/GALLERY — icon-card grid matching Landing.tsx's FeatureCard/About.tsx's PillarCard. */
const FeatureCardsSection: React.FC<{ content: ItemsContent; layout: LayoutConfig }> = ({ content, layout }) => {
  const theme = useTheme();
  const columns = Math.min(4, Math.max(1, layout.columns ?? 3));
  const mdWidth = 12 / columns;
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 }, backgroundColor: layout.backgroundColor || undefined }}>
      <SectionHeading title={content.title} subtitle={content.subtitle} alignment={layout.alignment} />
      <Grid container spacing={4}>
        {content.items?.map((item, index) => (
          <Grid item xs={12} sm={6} md={mdWidth} key={index}>
            <Card
              data-testid="cms-feature-card"
              elevation={0}
              sx={{
                height: '100%',
                border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: 'none',
                  borderColor: alpha(theme.palette.primary.main, 0.22),
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                    mb: 3,
                    fontSize: '1.5rem',
                    fontWeight: 700,
                  }}
                >
                  {index + 1}
                </Box>
                {item.title && (
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    {item.title}
                  </Typography>
                )}
                {item.description && (
                  <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                    {item.description}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

/** HOW_IT_WORKS — numbered step layout matching Landing.tsx's StepCard. */
const StepsSection: React.FC<{ content: ItemsContent; layout: LayoutConfig }> = ({ content, layout }) => {
  const theme = useTheme();
  return (
    <Box sx={{ backgroundColor: layout.backgroundColor || alpha(theme.palette.secondary.main, 0.55), py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <SectionHeading title={content.title} subtitle={content.subtitle} alignment={layout.alignment} />
        <Grid container spacing={6}>
          {content.items?.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box data-testid="cms-step" sx={{ position: 'relative', textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.primary.main,
                    color: '#ffffff',
                    fontSize: '2rem',
                    fontWeight: 700,
                    margin: '0 auto',
                    mb: 3,
                    }}
                >
                  {index + 1}
                </Box>
                {item.title && (
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    {item.title}
                  </Typography>
                )}
                {item.description && (
                  <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                    {item.description}
                  </Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

/** BENEFITS — checklist cards matching Landing.tsx's benefits section. */
const BenefitsSection: React.FC<{ content: ItemsContent; layout: LayoutConfig }> = ({ content, layout }) => {
  const theme = useTheme();
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 }, backgroundColor: layout.backgroundColor || undefined }}>
      <SectionHeading title={content.title} subtitle={content.subtitle} alignment={layout.alignment} />
      <Grid container spacing={6}>
        {content.items?.map((item, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card
              data-testid="cms-benefit-card"
              elevation={0}
              sx={{
                height: '100%',
                border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                p: 4,
                boxShadow: 'none',
              }}
            >
              {item.title && (
                <Typography variant="h5" gutterBottom fontWeight={700} color="primary">
                  {item.title}
                </Typography>
              )}
              {item.description && (
                <Stack direction="row" spacing={2} alignItems="flex-start" mt={3}>
                  <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 24, mt: 0.5 }} />
                  <Typography variant="body1" lineHeight={1.7}>
                    {item.description}
                  </Typography>
                </Stack>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

/** Elevated white card CTA matching Landing.tsx's Final CTA section. */
const CtaSection: React.FC<{ content: CtaContent; layout: LayoutConfig }> = ({ content, layout }) => {
  const theme = useTheme();
  return (
    <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: layout.backgroundColor || undefined }}>
      <Container maxWidth="md">
        <Stack
          data-testid="cms-cta-card"
          spacing={4}
          alignItems={alignItemsFor(layout.alignment)}
          textAlign={layout.alignment ?? 'center'}
          sx={{
            backgroundColor: 'background.paper',
            border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            borderRadius: 6,
            px: { xs: 3, md: 8 },
            py: { xs: 5, md: 7 },
            boxShadow: 'none',
          }}
        >
          {content.title && (
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, fontWeight: 700, color: 'text.primary' }}>
              {content.title}
            </Typography>
          )}
          {content.subtitle && (
            <Typography variant="h6" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
              {content.subtitle}
            </Typography>
          )}
          {content.buttonLabel && (
            <Button
              variant="contained"
              size="large"
              href={content.buttonHref}
              endIcon={<ArrowForwardIcon />}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: '#ffffff',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 700,
                mt: 2,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              {content.buttonLabel}
            </Button>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

const SanitizedHtml = React.lazy(() => import('./SanitizedHtml'));

const RichTextSection: React.FC<{ content: RichTextContent; layout: LayoutConfig }> = ({ content, layout }) => (
  <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 }, backgroundColor: layout.backgroundColor || undefined }}>
    <Stack spacing={2} sx={{ textAlign: layout.alignment ?? 'left' }}>
      {content.title && (
        <Typography variant="h3" fontWeight={700}>
          {content.title}
        </Typography>
      )}
      {content.body && (
        // Body is admin-authored HTML (Tiptap) — sanitized before rendering since it's shown
        // on the public site. Plain-text seed content (no HTML tags) renders unchanged.
        // The sanitizer is loaded on demand so pages without a rich-text section do not download it.
        <React.Suspense fallback={null}>
          <SanitizedHtml
            html={content.body}
            sx={{
              color: 'text.secondary',
              lineHeight: 1.8,
              whiteSpace: 'pre-wrap',
              fontSize: '1.05rem',
              '& img': { maxWidth: '100%', borderRadius: 1 },
            }}
          />
        </React.Suspense>
      )}
    </Stack>
  </Container>
);

const VideoSection: React.FC<{ content: VideoContent; layout: LayoutConfig }> = ({ content, layout }) => (
  <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 }, backgroundColor: layout.backgroundColor || undefined }}>
    <Stack spacing={2} alignItems={alignItemsFor(layout.alignment)}>
      {content.title && (
        <Typography variant="h3" fontWeight={700}>
          {content.title}
        </Typography>
      )}
      {content.videoUrl && (
        <Box component="video" controls src={content.videoUrl} sx={{ width: '100%', borderRadius: 2 }} />
      )}
    </Stack>
  </Container>
);

interface SectionRendererProps {
  sections: PublicCmsSectionResponse[];
}

export const SectionRenderer: React.FC<SectionRendererProps> = ({ sections }) => (
  <Box>
    {sections.map((section) => {
      const layout = parseLayout(section.layout);
      switch (section.sectionType) {
        case 'HERO': {
          const content = safeParse<HeroContent>(section.content);
          return content ? <HeroSection key={section.id} content={content} layout={layout} /> : null;
        }
        case 'HOW_IT_WORKS': {
          const content = safeParse<ItemsContent>(section.content);
          return content ? <StepsSection key={section.id} content={content} layout={layout} /> : null;
        }
        case 'BENEFITS': {
          const content = safeParse<ItemsContent>(section.content);
          return content ? <BenefitsSection key={section.id} content={content} layout={layout} /> : null;
        }
        case 'FEATURES':
        case 'CARDS':
        case 'GALLERY': {
          const content = safeParse<ItemsContent>(section.content);
          return content ? <FeatureCardsSection key={section.id} content={content} layout={layout} /> : null;
        }
        case 'FINAL_CTA':
        case 'CTA': {
          const content = safeParse<CtaContent>(section.content);
          return content ? <CtaSection key={section.id} content={content} layout={layout} /> : null;
        }
        case 'RICH_TEXT': {
          const content = safeParse<RichTextContent>(section.content);
          return content ? <RichTextSection key={section.id} content={content} layout={layout} /> : null;
        }
        case 'VIDEO': {
          const content = safeParse<VideoContent>(section.content);
          return content ? <VideoSection key={section.id} content={content} layout={layout} /> : null;
        }
        default:
          return null;
      }
    })}
  </Box>
);
